import {
  Component,
  TemplateRef,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  input,
  model,
} from '@angular/core';
import { CurrencyEnum, IncotermWithNscEnum } from '@app/generated/graphql';
import {
  CurrencyService,
  ICurrency,
} from '@app/core/services/currency.service';
import { Popover } from 'primeng/popover';
import { NgTemplateOutlet } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';

export type UnitType =
  | 'KG'
  | 'RAW CARTON'
  | 'STD CARTON'
  | 'PALLET'
  | 'CONTAINER';

// mirror the above type - used for validation and drop-down selection options
const UnitTypes: UnitType[] = [
  'KG',
  'RAW CARTON',
  'STD CARTON',
  'PALLET',
  'CONTAINER',
];

export interface NetReturnGranularity {
  unit: UnitType;
  currency: CurrencyEnum;
  incoterm: IncotermWithNscEnum;
  verboseBreakdown: boolean;
}

export const NET_RETURN_GRANULARITY_STORAGE_KEY = 'net_return_granularity';

export const loadNetReturnGranularity = (): NetReturnGranularity | null => {
  const jsonGranularity = localStorage.getItem(
    NET_RETURN_GRANULARITY_STORAGE_KEY,
  );

  if (jsonGranularity) {
    const parsedGranularity: NetReturnGranularity = JSON.parse(jsonGranularity);

    if (isValidNetReturnGranularity(parsedGranularity)) {
      return parsedGranularity;
    } else {
      console.log('invalid net return config in localStorage - using defaults');
    }
  }

  return null;
};

function isValidNetReturnGranularity(obj: any): obj is NetReturnGranularity {
  return (
    obj &&
    UnitTypes.includes(obj.unit) &&
    Object.values(CurrencyEnum).includes(obj.currency) &&
    Object.values(IncotermWithNscEnum).includes(obj.incoterm)
  );
}

@Component({
  selector: 'ag-net-return-granularity-picker',
  templateUrl: './net-return-granularity-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    Popover,
    NgSelectComponent,
    FormsModule,
  ],
})
export class NetReturnGranularityPickerComponent {
  private currencyService = inject(CurrencyService);

  readonly granularity = model<NetReturnGranularity>({
    unit: 'RAW CARTON',
    currency: CurrencyEnum.Zar,
    incoterm: IncotermWithNscEnum.Exw,
    verboseBreakdown: false,
  });

  // only show the Cost Breakdown checkbox if you're opening the granularity picker
  // on a page that actually has the Price Breakdown tooltip
  readonly sourcePageHasPriceBreakdown = input(false);

  // custom template
  /** Pass in a template to customize the trigger button */
  readonly customTriggerTemplateRef = input<TemplateRef<any> | undefined>(
    undefined,
  );

  @ViewChild('granularityPopover') granularityPopover: Popover;

  units: UnitType[] = UnitTypes;

  currencies: ICurrency[];

  incoterms = Object.values(IncotermWithNscEnum);

  constructor() {
    this.currencies = this.currencyService.allowedCurrencies;
  }

  onGranularityChange() {
    const granularity = this.granularity();
    localStorage.setItem(
      NET_RETURN_GRANULARITY_STORAGE_KEY,
      JSON.stringify(granularity),
    );
    // Re-set so model output notifies two-way parents after in-place ngModel edits
    this.granularity.set({ ...granularity });
  }

  onTriggerClick(event: Event): void {
    event.stopImmediatePropagation();

    if (!this.granularityPopover) {
      return;
    }

    this.granularityPopover.toggle(event);
  }
}
